import { Chart } from '../Enums/Chart.enum';
import { Languages } from '../../../Enums/Common';
import moment from 'moment-timezone';

interface defaultOptions {
	xScale?: object;
	yScale?: object;
	plugins?: object;
}

const DEFAULT_Y_SCALE = {
	beginAtZero: true,
	ticks: {
		fontSize: 18,
	},
	step: 10,
};

const DEFAULT_PUBLIC = {
	spanGaps: true,
	responsive: true,
	maintainAspectRatio: false,
};

export const getOptionsForChartByName = (
	chartName: Chart,
	view: string,
	t: any,
	language: Languages,
	dateFormat: string,
) => {
	let labelFormat = t(dateFormat) + ' HH:mm';

	switch (view) {
		case 'day':
			labelFormat = 'HH:mm';
			break;
		case 'week':
			labelFormat = 'DD.MM';
			break;
		case 'month':
			labelFormat = 'DD.MM';
			break;
		case 'year':
			labelFormat = 'MMM YYYY';
	}

	const DEFAULT_X_SCALE = {
		ticks: {
			callback: function (val) {
				return moment(this.getLabelForValue(val), 'x').format(labelFormat);
			},
		},
	};

	const DEFAULT_PLUGINS = {
		legend: {
			position: 'top',
		},
		tooltip: {
			callbacks: {
				title: (context: any) =>
					moment(context[0].label, 'x').format(labelFormat),
				afterTitle: (context: any) =>
					context[0].dataset.sources[context[0].dataIndex],
				label: (context: any) =>
					context.formattedValue + ' ' + context?.dataset?.unit,
			},
		},
	};

	const getDefaults = ({
		xScale = DEFAULT_X_SCALE,
		yScale = DEFAULT_Y_SCALE,
		plugins = DEFAULT_PLUGINS,
	}: defaultOptions) => {
		return {
			options: {
				...DEFAULT_PUBLIC,
				scales: {
					x: xScale,
					y: yScale,
				},
				plugins: plugins,
			},
		};
	};

	const getYScale = ({
		title = { display: false, text: '', color: '', fontWeight: 500 },
		step = 10,
		min = null,
		max = null,
	}) => {
		return {
			beginAtZero: true,
			ticks: {
				fontSize: 18,
			},
			title: title,
			step: step,
			min: min,
			max: max,
			offset: true,
		};
	};

	const CHART_OPTIONS = {
		[Chart.BLOOD_GLUCOSE]: getDefaults({
			yScale: getYScale({
				min: 0,
				step: 50,
			}),
			plugins: {
				legend: {
					position: 'top',
				},
				tooltip: {
					callbacks: {
						beforeTitle: function (context: any) {
							return context[0].dataset.questionnaires[context[0].dataIndex];
						},
						title: function (context: any) {
							// return format(new Date(context[0].label), labelFormat);
							return moment(context[0].label, 'x').format(labelFormat);
						},
						label: function (context: any) {
							return context.formattedValue + ' ' + context?.dataset?.unit;
						},
					},
				},
			},
		}),
		[Chart.BLOOD_PRESSURE]: getDefaults({
			yScale: getYScale({}),
		}),
		[Chart.BLOOD_PRESSURE_SYS_DIA]: getDefaults({
			yScale: getYScale({}),
			plugins: {
				legend: {
					position: 'top',
				},
				tooltip: {
					callbacks: {
						beforeTitle: function (context: any) {
							return moment(context[0].label, 'x').format(labelFormat);
						},
						title: function (context: any) {
							return (
								context[0].dataset.questionnaires[context[0].dataIndex] ??
								context[0].dataset.sources[context[0].dataIndex]
							);
						},
						label: function (context: any) {
							return context.formattedValue + ' ' + context?.dataset?.unit;
						},
					},
				},
			},
		}),
		[Chart.WEIGHT]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: 'kg',
					color: '#1665d8',
					fontWeight: 500,
				},
			}),
			plugins: {
				legend: {
					position: 'top',
				},
				tooltip: {
					callbacks: {
						beforeTitle: function (context: any) {
							return moment(context[0].label, 'x').format(labelFormat);
						},
						title: function (context: any) {
							return (
								context[0].dataset.questionnaires[context[0].dataIndex] ??
								context[0].dataset.sources[context[0].dataIndex]
							);
						},
						label: function (context: any) {
							return context.formattedValue + ' ' + context?.dataset?.unit;
						},
					},
				},
			},
		}),
		[Chart.HEIGHT]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: 'cm',
					color: '#1665d8',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.WAISTLINE]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: 'cm',
					color: '#1665d8',
					fontWeight: 500,
				},
				step: 5,
			}),
		}),
		[Chart.BREATHING_RATE]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.breathingRate'),
					color: '#e39d58',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.STEPS]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.filter.steps'),
					color: '#6d16d8',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.SLEEP]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.sleep'),
					color: '#3c3052',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.OXYGEN_SATURATION]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.oxygenSaturation'),
					color: '#90e2f1',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.HEMOGLOBIN]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.hemoglobin'),
					color: '#3c3052',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.HEMOGLOBIN_A1C]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.hemoglobinA1C'),
					color: '#3c3052',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.STRESS_LEVEL]: getDefaults({}),
		[Chart.ACTIVE_ENERGY]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.activeEnergy'),
					color: '#f56838',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.DISTANCE_WALKING_RUNNING]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.distanceWalkingRunning'),
					color: '#f5d91e',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.WORKOUT]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('component.chart.unit.workout'),
					color: '#327229',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.ACTUAL_WEIGHT]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('base.actualWeight'),
					color: '#327229',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.DIASTOLIC_BLOOD_PRESSURE]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('base.diastolicBloodPressure'),
					color: '#327229',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.SYSTOLIC_BLOOD_PRESSURE]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('base.systolicBloodPressure'),
					color: '#327229',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.TOTAL_NUMBER_OF_PREGNANCIES]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('base.totalNumberOfPregnencies'),
					color: '#327229',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.WEIGHT_BEFORE_PREGNANCY]: getDefaults({
			yScale: getYScale({
				title: {
					display: true,
					text: t('base.weightBeforePregnancy'),
					color: '#327229',
					fontWeight: 500,
				},
			}),
		}),
		[Chart.ESAS]: getDefaults({
			yScale: getYScale({
				title: {
					display: false,
					text: 'ESAS',
					color: '#327229',
					fontWeight: 500,
				},
			}),
			plugins: {
				tooltip: {
					callbacks: {
						title: function (context: any) {
							return null;
						},
						label: function (context: any) {
							return context?.dataset?.label + ' ' + context.formattedValue;
						},
					},
				},
			},
		}),
	};

	return CHART_OPTIONS[chartName].options;
};
